import React, { useState, useEffect } from 'react';
import { Check, X, Users, User, ChevronDown, Sparkles, Building2 } from 'lucide-react';
import { createCheckoutSession } from '../../lib/stripe';
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_TIERS,
  SubscriptionPlan,
  getCurrentSubscriptionUsage,
} from '../../types/subscriptions';
import { cn } from '../../lib/utils';
import { useUser } from '../../hooks/queries/useAuth';
import { handleError } from '../../utils/errorHandling';
import { secureApi } from '../../api/secure';
// import { StripeDiagnostics } from './StripeDiagnostics'; // Uncomment for development/debugging

interface PricingPlansProps {
  initialBillingInterval?: 'month' | 'year';
  isLandingPage?: boolean;
  onSignUp?: () => void;
}

export function PricingPlans({
  initialBillingInterval = 'year',
  isLandingPage = false,
  onSignUp,
}: PricingPlansProps) {
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>(initialBillingInterval);
  const [teamSize, setTeamSize] = useState<'team_small' | 'team_medium' | 'team_large'>(
    'team_small'
  );
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const { data: user } = useUser();
  
  // Fetch current subscription tier on component mount
  useEffect(() => {
    if (user) {
      const fetchCurrentTier = async () => {
        try {
          const usage = await getCurrentSubscriptionUsage();
          setCurrentTier(usage.currentTier || 'free');
        } catch (error) {
          console.error('Error fetching current subscription tier:', error);
          // Default to free if we can't determine the current tier
          setCurrentTier('free');
        }
      };
      
      fetchCurrentTier();
    }
  }, [user]);

  // Get plans for current billing interval
  const getPlansForInterval = () => {
    return Object.values(SUBSCRIPTION_PLANS).filter(plan => plan.interval === billingInterval);
  };

  // Group plans by category
  const plans = getPlansForInterval();
  const proPlans = plans.filter(plan => plan.tier === 'pro');

  // Get the current team plan based on selected team size
  const getTeamPlan = () => {
    const planId =
      teamSize === 'team_small'
        ? billingInterval === 'month'
          ? 'SMALL_TEAM_MONTHLY'
          : 'SMALL_TEAM_ANNUAL'
        : teamSize === 'team_medium'
          ? billingInterval === 'month'
            ? 'MEDIUM_TEAM_MONTHLY'
            : 'MEDIUM_TEAM_ANNUAL'
          : billingInterval === 'month'
            ? 'LARGE_TEAM_MONTHLY'
            : 'LARGE_TEAM_ANNUAL';

    return SUBSCRIPTION_PLANS[planId];
  };

  const teamPlan = getTeamPlan();

  // Handle subscription or sign up based on context
  const handleAction = async (plan: SubscriptionPlan) => {
    // If we're on the landing page, trigger the sign up callback
    if (isLandingPage && onSignUp) {
      onSignUp();
      return;
    }

    // Otherwise handle the subscription flow
    try {
      // Check if the user is logged in first
      if (!user) {
        alert('Please log in to subscribe to a plan');
        return;
      }

      console.log('Requesting subscription to plan:', plan.name, 'with price ID:', plan.stripePriceId);
      
      // Show loading state to user
      const button = document.activeElement;
      if (button instanceof HTMLButtonElement) {
        const originalText = button.textContent;
        button.disabled = true;
        button.textContent = 'Processing...';
        
        const resetButton = () => {
          button.disabled = false;
          button.textContent = originalText || 'Subscribe';
        };
        
        try {
          // Check if this is a downgrade from current tier
          if (
            currentTier && 
            plan.tier && 
            currentTier !== 'free' && 
            plan.tier !== 'vip' &&
            currentTier !== plan.tier
          ) {
            const isDowngrade = isDowngradedTier(currentTier, plan.tier);
            
            if (isDowngrade) {
              console.log('This is a plan downgrade, validating eligibility...');
              setIsValidating(true);
              
              // Use the server-side validation to check if downgrade is allowed
              const validationResult = await secureApi.validatePlanDowngrade(plan.tier);
              setIsValidating(false);
              
              if (!validationResult.valid) {
                // Display error notification with the reason
                handleError('Plan Downgrade', new Error(validationResult.reason || 'You cannot downgrade to this plan with your current usage.'), {
                  showNotification: true,
                  notificationPrefix: 'Cannot Downgrade Plan',
                  callback: resetButton,
                });
                return;
              }
              
              console.log('Downgrade validation passed, continuing with subscription change');
            }
          }
          
          // If validation passes or is not needed, create checkout session
          await createCheckoutSession(plan.stripePriceId);
        } catch (e: unknown) {
          // Handle error but continue throwing for outer catch
          handleError('Subscription API call', e, {
            showNotification: true,
            notificationPrefix: 'Subscription failed',
            callback: resetButton,
          });
          throw e;
        }
      } else {
        await createCheckoutSession(plan.stripePriceId);
      }
    } catch (error: unknown) {
      setIsValidating(false);
      handleError('Creating subscription', error, {
        showNotification: true,
        notificationPrefix: 'Subscription error',
      });
    }
  };
  
  // Helper function to determine if a tier change is a downgrade
  const isDowngradedTier = (currentTier: string, targetTier: string): boolean => {
    // Tier order from lowest to highest
    const tierOrder = ['free', 'pro', 'team_small', 'team_medium', 'team_large', 'vip'];
    
    // Get indices (normalizing to lowercase)
    const currentIndex = tierOrder.indexOf(currentTier.toLowerCase());
    const targetIndex = tierOrder.indexOf(targetTier.toLowerCase());
    
    // If target tier has a lower index than current tier, it's a downgrade
    return targetIndex < currentIndex;
  };

  // Get the appropriate button text based on the context
  const getButtonText = () => {
    return isLandingPage ? 'Sign Up' : 'Subscribe';
  };

  return (
    <div className="w-full px-2 sm:px-4">
      {/* Billing toggle and debug tools */}
      <div className="flex flex-col items-center mb-8">
        <div className="inline-flex items-center bg-gray-800 p-1 rounded-lg mb-2">
          <button
            onClick={() => setBillingInterval('month')}
            className={cn(
              'px-4 py-2 rounded-md transition-colors',
              billingInterval === 'month'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            )}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval('year')}
            className={cn(
              'px-4 py-2 rounded-md transition-colors',
              billingInterval === 'year'
                ? 'bg-blue-600 text-white'
                : 'text-gray-300 hover:text-white'
            )}
          >
            Yearly (Save ~16%)
          </button>
        </div>

        {/* Stripe Diagnostics Component (uncomment for development/debugging) 
        <StripeDiagnostics />
        */}
      </div>

      {/* Plans grid with headers and columns aligned */}
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:gap-4">
        {/* Solo plans column with header */}
        <div className="w-full md:w-1/2">
          {/* Solo header */}
          <div className="rounded-lg bg-blue-900/20 border border-blue-900/30 py-3 px-4 mb-4 text-center">
            <h2 className="text-xl font-bold text-white flex items-center justify-center">
              <User size={18} className="text-blue-400 mr-2" />
              Solo Plans
            </h2>
            <p className="text-sm text-gray-300">For individual use only</p>
          </div>

          {/* Solo plans grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4 h-full">
            {/* Free Plan */}
            <div
              className="bg-gradient-to-br from-gray-900 to-blue-900/40 rounded-xl shadow-lg overflow-hidden border border-blue-800/30 flex flex-col"
              style={{ height: '650px' }}
            >
              <div className="p-6">
                {/* Card Header - Fixed Height: 90px */}
                <div style={{ height: '90px' }} className="text-center">
                  <div className="mb-3 justify-center flex flex-col items-center">
                    <Sparkles size={36} className="text-blue-400 mb-3" />
                    <h3 className="text-xl font-bold text-white">Discovery</h3>
                  </div>
                  <p className="text-gray-300 text-sm">For personal projects</p>
                </div>

                {/* Spacing between header and price */}
                <div style={{ height: '30px' }}></div>

                {/* Price Display - Fixed Height: 80px */}
                <div style={{ height: '80px' }} className="flex items-center justify-center">
                  <div className="text-center">
                    <span className="text-5xl font-bold text-white">Free</span>
                  </div>
                </div>

                {/* Empty Selector Space - Fixed Height: 95px - matches team card height */}
                <div style={{ height: '95px' }} className="mb-4">
                  {/* Empty space where team selector would be */}
                </div>

                {/* Button Area - Fixed Height: 50px */}
                <div style={{ height: '50px' }}>
                  {isLandingPage ? (
                    <button
                      onClick={onSignUp}
                      className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Sign Up
                    </button>
                  ) : (
                    <button
                      disabled
                      className="w-full py-3 px-4 bg-gray-700 text-gray-300 rounded-lg cursor-not-allowed"
                    >
                      Current Plan
                    </button>
                  )}
                </div>
              </div>
              <div className="border-t border-gray-700 p-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Up to 3 projects</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Advanced AI features</span>
                  </li>
                  <li className="flex items-start">
                    <X size={18} className="text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-400">Priority support</span>
                  </li>
                  <li className="flex items-start">
                    <X size={18} className="text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-400">Team collaboration</span>
                  </li>
                  <li className="flex items-start invisible">
                    <X size={18} className="text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-400">Custom integrations</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pro Plan */}
            {proPlans.map(plan => (
              <div
                key={plan.id}
                className="bg-gradient-to-br from-gray-900 to-blue-800/70 rounded-xl shadow-lg overflow-hidden border border-blue-700/40 flex flex-col"
                style={{ height: '650px' }}
              >
                <div className="p-6">
                  {/* Card Header - Fixed Height: 90px */}
                  <div style={{ height: '90px' }} className="text-center">
                    <div className="mb-3 justify-center flex flex-col items-center">
                      <User size={36} className="text-blue-400 mb-3" />
                      <h3 className="text-xl font-bold text-white">Pro</h3>
                    </div>
                    <p className="text-gray-300 text-sm">Unlimited projects for individuals</p>
                  </div>

                  {/* Spacing between header and price */}
                  <div style={{ height: '30px' }}></div>

                  {/* Price Display - Fixed Height: 80px */}
                  <div style={{ height: '80px' }} className="flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-5xl font-bold text-white">€{plan.price}</span>
                      <span className="text-gray-400">/{plan.interval}</span>
                    </div>
                  </div>

                  {/* Empty Selector Space - Fixed Height: 95px - matches team card height */}
                  <div style={{ height: '95px' }} className="mb-4">
                    {/* Empty space where team selector would be */}
                  </div>

                  {/* Button Area - Fixed Height: 50px */}
                  <div style={{ height: '50px' }}>
                    <button
                      onClick={() => handleAction(plan)}
                      disabled={isValidating}
                      className={cn(
                        "w-full py-3 px-4 rounded-lg transition-colors",
                        isValidating 
                          ? "bg-blue-500 text-white cursor-wait" 
                          : "bg-blue-600 text-white hover:bg-blue-700"
                      )}
                    >
                      {isValidating ? 'Validating...' : getButtonText()}
                    </button>
                  </div>
                </div>
                <div className="border-t border-gray-700 p-6">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-300">Unlimited projects</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-300">Advanced AI features</span>
                    </li>
                    <li className="flex items-start">
                      <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-300">Priority support</span>
                    </li>
                    <li className="flex items-start">
                      <X size={18} className="text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-400">Team collaboration</span>
                    </li>
                    <li className="flex items-start invisible">
                      <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-300">Custom integrations</span>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Team plans column with header */}
        <div className="w-full md:w-1/2">
          {/* Team header */}
          <div className="rounded-lg bg-purple-900/20 border border-purple-900/30 py-3 px-4 mb-4 text-center">
            <h2 className="text-xl font-bold text-white flex items-center justify-center">
              <Users size={18} className="text-purple-400 mr-2" />
              Team Plans
            </h2>
            <p className="text-sm text-gray-300">For collaboration with team members</p>
          </div>

          {/* Team plans grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 lg:grid-cols-2 gap-4 h-full">
            {/* Consolidated Team Plan with dropdown */}
            <div
              className="bg-gradient-to-br from-gray-900 to-purple-900/60 rounded-xl shadow-lg overflow-hidden border border-purple-800/30"
              style={{ height: '650px' }}
            >
              <div className="p-6">
                {/* Card Header - Fixed Height: 90px */}
                <div style={{ height: '90px' }} className="text-center">
                  <div className="mb-3 justify-center flex flex-col items-center">
                    <Users size={36} className="text-purple-400 mb-3" />
                    <h3 className="text-xl font-bold text-white">Team</h3>
                  </div>
                  <p className="text-gray-300 text-sm">Team collaboration</p>
                </div>

                {/* Spacing between header and price */}
                <div style={{ height: '30px' }}></div>

                {/* Price Display - Fixed Height: 80px */}
                <div style={{ height: '80px' }} className="flex items-center justify-center">
                  <div className="text-center">
                    <span className="text-5xl font-bold text-white">€{teamPlan.price}</span>
                    <span className="text-gray-400">/{teamPlan.interval}</span>
                  </div>
                </div>

                {/* Team Size Selector - Positioned to align with other cards */}
                <div style={{ height: '55px' }}></div>

                {/* Team Size Selector - Fixed Height: 40px */}
                <div style={{ height: '40px' }} className="mb-4">
                  <div className="relative">
                    <select
                      value={teamSize}
                      onChange={e =>
                        setTeamSize(e.target.value as 'team_small' | 'team_medium' | 'team_large')
                      }
                      className="appearance-none w-full bg-gray-700 border-[3px] border-purple-500/70 text-white py-2 px-3 rounded-md focus:outline-none focus:ring-0 hover:border-purple-400/90 focus:border-[3px] focus:border-purple-500/90 transition-colors"
                    >
                      <option value="team_small">Starter (5 members)</option>
                      <option value="team_medium">Growth (20 members)</option>
                      <option value="team_large">Gold (50 members)</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-purple-400">
                      <ChevronDown size={16} />
                    </div>
                  </div>
                </div>

                {/* Button Area - Fixed Height: 50px */}
                <div style={{ height: '50px' }}>
                  <button
                    onClick={() => handleAction(teamPlan)}
                    disabled={isValidating}
                    className={cn(
                      "w-full py-3 px-4 rounded-lg transition-colors",
                      isValidating 
                        ? "bg-purple-500 text-white cursor-wait" 
                        : "bg-purple-600 text-white hover:bg-purple-700"
                    )}
                  >
                    {isValidating ? 'Validating...' : getButtonText()}
                  </button>
                </div>
              </div>
              <div className="border-t border-gray-700 p-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Unlimited projects</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Advanced AI features</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Priority support</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Team collaboration</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">
                      Up to {SUBSCRIPTION_TIERS[teamSize].maxCollaborators} members
                    </span>
                  </li>
                  {/* Basic integration line removed */}
                </ul>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div
              className="bg-gradient-to-br from-gray-900 via-purple-900 to-purple-700 rounded-xl shadow-xl overflow-hidden border border-purple-700/50"
              style={{ height: '650px' }}
            >
              <div className="p-6">
                {/* Card Header - Fixed Height: 90px */}
                <div style={{ height: '90px' }} className="text-center">
                  <div className="mb-3 justify-center flex flex-col items-center">
                    <Building2 size={36} className="text-purple-400 mb-3" />
                    <h3 className="text-xl font-bold text-white">Enterprise</h3>
                  </div>
                  <p className="text-gray-300 text-sm">Custom solutions for organizations</p>
                </div>

                {/* Spacing between header and price */}
                <div style={{ height: '30px' }}></div>

                {/* Price Display - Fixed Height: 80px */}
                <div style={{ height: '80px' }} className="flex items-center justify-center">
                  <div className="text-center">
                    <span className="text-5xl font-bold text-white">Custom</span>
                  </div>
                </div>

                {/* Empty Selector Space - Fixed Height: 95px - matches team card height */}
                <div style={{ height: '95px' }} className="mb-4">
                  {/* Empty space to align with team dropdown */}
                </div>

                {/* Button Area - Fixed Height: 50px */}
                <div style={{ height: '50px' }}>
                  <a
                    href="mailto:contact@zentime.io"
                    className="block w-full py-3 px-4 bg-white text-gray-900 rounded-lg text-center hover:bg-gray-100 transition-colors"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
              <div className="border-t border-gray-700 p-6">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Unlimited projects</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Advanced AI features</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Priority support</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Team collaboration</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Unlimited members</span>
                  </li>
                  <li className="flex items-start">
                    <Check size={18} className="text-green-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm text-gray-300">Custom Code</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}